
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useStableAuth } from '@/hooks/useStableAuth';
import { supabase } from '@/integrations/supabase/client';

export interface UserDisplayInfo {
  username: string;
  first_name: string | null;
  last_name: string | null;
}

interface UserDisplayContextProps {
  displayInfo: UserDisplayInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const UserDisplayContext = createContext<UserDisplayContextProps>({
  displayInfo: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

export const useUserDisplayInfo = () => useContext(UserDisplayContext);

interface UserDisplayProviderProps {
  children: React.ReactNode;
}

// Session storage key for caching
const DISPLAY_INFO_CACHE_KEY = 'userDisplayInfo';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_VERSION = 'v2';

export const UserDisplayProvider: React.FC<UserDisplayProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useStableAuth();
  const [displayInfo, setDisplayInfo] = useState<UserDisplayInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFromCache = useCallback((): UserDisplayInfo | null => {
    try {
      const cached = sessionStorage.getItem(DISPLAY_INFO_CACHE_KEY);
      if (cached) {
        const { data, timestamp, version } = JSON.parse(cached);
        
        if (version !== CACHE_VERSION) {
          sessionStorage.removeItem(DISPLAY_INFO_CACHE_KEY);
          return null;
        }
        
        if (Date.now() - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (error) {
      sessionStorage.removeItem(DISPLAY_INFO_CACHE_KEY);
    }
    return null;
  }, []);

  const saveToCache = useCallback((data: UserDisplayInfo) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      sessionStorage.setItem(DISPLAY_INFO_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('UserDisplayContext: Cache write error:', error);
    }
  }, []);

  const fetchDisplayInfo = useCallback(async () => {
    if (!user || !isAuthenticated) {
      setDisplayInfo(null);
      setLoading(false);
      return;
    }

    // Try to load from cache first
    const cachedData = loadFromCache();
    if (cachedData) {
      setDisplayInfo(cachedData);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase.rpc('get_user_display_info');
      
      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        const userDisplayInfo: UserDisplayInfo = {
          username: data[0].username,
          first_name: data[0].first_name,
          last_name: data[0].last_name
        };
        
        setDisplayInfo(userDisplayInfo);
        saveToCache(userDisplayInfo);
      } else {
        setError('Display information not found');
      }
    } catch (error: any) {
      console.error('UserDisplayContext: Error fetching display info:', error);
      const errorMessage = error.message || 'Failed to load display information';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAuthenticated, loadFromCache, saveToCache]);

  const refetch = useCallback(async () => {
    sessionStorage.removeItem(DISPLAY_INFO_CACHE_KEY);
    await fetchDisplayInfo();
  }, [fetchDisplayInfo]);

  useEffect(() => {
    fetchDisplayInfo();
  }, [fetchDisplayInfo]);

  const value = {
    displayInfo,
    loading,
    error,
    refetch
  };

  return (
    <UserDisplayContext.Provider value={value}>
      {children}
    </UserDisplayContext.Provider>
  );
};
